import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { getEnv } from "@/config/env";
import { Document, DocumentTopic } from "@/types/models";
import { evidenceBaseApi, DuplicateCheckItem, DuplicateCheckResult } from "@/services/evidenceBaseApi";

const EVIDENCE_BASE_API_URL =
  getEnv("VITE_EVIDENCE_BASE_API_URL") ||
  "https://jca-heor-service-evidence-base.bluegrass-b6db7524.eastus.azurecontainerapps.io";

// --- API response type (snake_case from backend) ---

export interface DocumentApiResponse {
  id: string;
  filename: string | null;
  title: string | null;
  authors: string | null;
  year: string | null;
  description: string | null;
  source_type: string | null;
  organization: string | null;
  country: string | null;
  evidence_type: string | null;
  data_retention: string | null;
  url: string | null;
  language: string | null;
  ownership: string | null;
  client_id: string | null;
  tags: string | null;
  topics: DocumentTopic[] | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function toDocument(r: DocumentApiResponse): Document {
  return {
    id: r.id,
    filename: r.filename || "",
    title: r.title || r.filename || "",
    author: r.authors || "",
    description: r.description || "",
    tags: r.tags ? r.tags.split(",").map((t) => t.trim()) : [],
    topics: r.topics ?? [],
    sourceType: r.source_type || "",
    sourceOrganisation: r.organization || "",
    country: r.country || "",
    year: r.year || "",
    evidenceType: r.evidence_type || "",
    dataRetention: r.data_retention || null,
    url: r.url || "",
    language: r.language || "",
    ownership: r.ownership || "",
    clientId: r.client_id ?? null,
    createdBy: r.created_by ?? null,
    createdAt: r.created_at,
  };
}

// --- Fetch documents ---

async function fetchDocuments(
  getAccessToken: () => Promise<string | null>
): Promise<Document[]> {
  const token = await getAccessToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${EVIDENCE_BASE_API_URL}/api/evidence/documents`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error("Unauthorized");
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || `Fetch failed: ${response.status}`);
  }

  const data: DocumentApiResponse[] = await response.json();
  return data.map(toDocument);
}

const EMPTY_DOCUMENTS: Document[] = [];

export function useDocuments() {
  const { getAccessToken } = useAuth();

  const query = useQuery({
    queryKey: ["documents"],
    queryFn: () => fetchDocuments(getAccessToken),
  });

  return { ...query, data: query.data ?? EMPTY_DOCUMENTS };
}

// --- Create documents ---

export interface DocumentCreatePayload {
  filename: string;
  title: string | null;
  authors: string | null;
  year: string | null;
  description: string | null;
  source_type: string | null;
  organization: string | null;
  country: string | null;
  evidence_type: string | null;
  data_retention: string | null;
  url: string | null;
  language: string | null;
  ownership: string | null;
  client_id: string | null;
  tags: string | null;
  topic_ids: string[] | null;
}

async function createDocument(
  payload: DocumentCreatePayload,
  getAccessToken: () => Promise<string | null>
): Promise<DocumentApiResponse> {
  const token = await getAccessToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${EVIDENCE_BASE_API_URL}/api/evidence/documents`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (response.status === 401) throw new Error("Unauthorized");
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      errorBody.detail || `Metadata save failed: ${response.status}`
    );
  }

  return response.json();
}

async function createDocuments(
  payloads: DocumentCreatePayload[],
  getAccessToken: () => Promise<string | null>
) {
  return Promise.all(
    payloads.map((payload) => createDocument(payload, getAccessToken))
  );
}

export function useSaveDocumentMetadata() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payloads: DocumentCreatePayload[]) =>
      createDocuments(payloads, getAccessToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

// --- Update document ---

export interface DocumentUpdatePayload {
  filename?: string | null;
  title?: string | null;
  authors?: string | null;
  year?: string | null;
  description?: string | null;
  source_type?: string | null;
  organization?: string | null;
  country?: string | null;
  evidence_type?: string | null;
  data_retention?: string | null;
  url?: string | null;
  language?: string | null;
  ownership?: string | null;
  client_id?: string | null;
  tags?: string | null;
  topic_ids?: string[] | null;
}

async function updateDocument(
  id: string,
  payload: DocumentUpdatePayload,
  getAccessToken: () => Promise<string | null>
) {
  const token = await getAccessToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${EVIDENCE_BASE_API_URL}/api/evidence/documents/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (response.status === 401) throw new Error("Unauthorized");
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      errorBody.detail || `Update failed: ${response.status}`
    );
  }

  return response.json();
}

export function useUpdateDocument() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: DocumentUpdatePayload & { id: string }) =>
      updateDocument(id, payload, getAccessToken),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["documents"] });
    },
  });
}

// --- Delete document ---

async function deleteDocument(
  id: string,
  getAccessToken: () => Promise<string | null>
): Promise<void> {
  const token = await getAccessToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${EVIDENCE_BASE_API_URL}/api/evidence/documents/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error("Unauthorized");
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || `Delete failed: ${response.status}`);
  }
}

export function useDeleteDocument() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDocument(id, getAccessToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

// --- Check duplicates ---

export function useCheckDuplicates() {
  return useMutation({
    mutationFn: ({ items, visibleIds }: { items: DuplicateCheckItem[]; visibleIds?: string[] }): Promise<DuplicateCheckResult[]> =>
      evidenceBaseApi.checkDuplicates(items, visibleIds),
  });
}
